import { CLASSIC_BASE, FINISHES, STAUNTON } from "@/components/board/piece-shapes";
import { BOARDS, type BoardFinish } from "@/lib/appearance";

const FILES = "abcdefgh";

export type DiagramOptions = {
  /** Draw from Black's side (a8 bottom right). */
  flip?: boolean;
  /** Squares to tint, such as the last move or a target. */
  highlight?: string[];
  /** File letters and rank numbers inside the edge squares. */
  coords?: boolean;
  /** Square colours; walnut unless chosen. */
  board?: BoardFinish;
};

/**
 * The shared drawing kit: piece gradients, the checkerboard and one symbol per piece.
 * A page with several diagrams renders it once (DiagramSprite) and every board refers
 * to it with <use>; a standalone image (a social card) embeds it.
 */
export function diagramDefs(board: BoardFinish = "walnut") {
  const colours = BOARDS[board];
  const gradient = (color: "w" | "b") => {
    const p = FINISHES.ivory[color];
    return `<linearGradient id="dg-body-${color}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${p.dark}"/><stop offset="0.4" stop-color="${p.light}"/><stop offset="1" stop-color="${p.middle}"/></linearGradient>`;
  };
  const symbols = (["w", "b"] as const).flatMap((color) =>
    Object.entries(STAUNTON).map(([type, shape]) => {
      const p = FINISHES.ivory[color];
      return (
        `<symbol id="dg-${color}${type}" viewBox="0 0 100 100">` +
        `<path d="${shape.outline}" fill="url(#dg-body-${color})" stroke="${p.edge}" stroke-width="2" stroke-linejoin="round"/>` +
        `<path d="${CLASSIC_BASE}" fill="url(#dg-body-${color})" stroke="${p.edge}" stroke-width="2" stroke-linejoin="round"/>` +
        `<path d="${shape.carving}" fill="none" stroke="${p.carving}" stroke-width="1.6" stroke-linecap="round" opacity="0.8"/>` +
        `</symbol>`
      );
    }),
  );
  // a1 is dark from either side, so one pattern serves both orientations.
  const checker = `<pattern id="dg-checker" width="200" height="200" patternUnits="userSpaceOnUse"><rect width="200" height="200" fill="${colours.light}"/><rect y="100" width="100" height="100" fill="${colours.dark}"/><rect x="100" width="100" height="100" fill="${colours.dark}"/></pattern>`;
  return `<defs>${gradient("w")}${gradient("b")}${checker}${symbols.join("")}</defs>`;
}

/**
 * A static board diagram as an SVG string. With `standalone` (the default) the drawing
 * kit is embedded; otherwise the page must render DiagramSprite once.
 */
export function boardSvg(fen: string, { flip = false, highlight = [], coords = false, board = "walnut", standalone = true }: DiagramOptions & { standalone?: boolean } = {}) {
  const colours = BOARDS[board];
  const at = (file: number, rank: number) => (flip ? { x: (7 - file) * 100, y: rank * 100 } : { x: file * 100, y: (7 - rank) * 100 });
  const parts = [`<rect width="800" height="800" fill="url(#dg-checker)"/>`];
  for (const sq of highlight) {
    const { x, y } = at(FILES.indexOf(sq[0]), Number(sq[1]) - 1);
    parts.push(`<rect x="${x}" y="${y}" width="100" height="100" fill="#e0a43a" opacity="0.45"/>`);
  }
  if (coords) {
    const ink = (file: number, rank: number) => ((file + rank) % 2 === 1 ? colours.dark : colours.light);
    const edge = flip ? 7 : 0;
    for (let i = 0; i < 8; i++) {
      const f = at(i, edge);
      const r = at(edge, i);
      parts.push(`<text x="${f.x + 92}" y="${f.y + 94}" font-size="16" font-family="sans-serif" text-anchor="end" fill="${ink(i, edge)}">${FILES[i]}</text>`);
      parts.push(`<text x="${r.x + 6}" y="${r.y + 20}" font-size="16" font-family="sans-serif" fill="${ink(edge, i)}">${i + 1}</text>`);
    }
  }
  fen.split(" ")[0].split("/").forEach((row, r) => {
    let file = 0;
    for (const ch of row) {
      if (/\d/.test(ch)) {
        file += Number(ch);
        continue;
      }
      const { x, y } = at(file, 7 - r);
      parts.push(`<use href="#dg-${ch === ch.toUpperCase() ? "w" : "b"}${ch.toLowerCase()}" x="${x}" y="${y}" width="100" height="100"/>`);
      file += 1;
    }
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800" width="800" height="800">${standalone ? diagramDefs(board) : ""}${parts.join("")}</svg>`;
}
